import React, { useState, useEffect } from 'react';
import {
  BehavioralDomainId,
} from '../sync/target-types';
import {
  ComposableBlock,
  ComposableBlockType,
  InterventionRecipe,
  SomaticBlockConfig,
  CognitiveBlockConfig,
  FrictionBlockConfig,
  SupportBlockConfig,
  DEFAULT_DOMAIN_RECIPES,
  createDefaultRecipeForDomain,
  createDefaultSomaticBlock,
  createDefaultCognitiveBlock,
  createDefaultFrictionBlock,
  createDefaultSupportBlock,
  reorderBlocks,
  addBlockToRecipe,
  removeBlockFromRecipe,
  updateBlockInRecipe,
  assignRecipeToClient,
  validateRecipe,
} from '../sync/recipe-types';
import { VoiceMemoModal } from './audio/VoiceMemoModal';

export interface InterventionConfig {
  targetDomain: string;
  category: string;
  cbtReframingPrompt: string;
  mediaType: 'none' | 'audio' | 'video';
  mediaTitle: string;
  mediaUrl: string;
  breathingIntervalSeconds: {
    inhale: number;
    hold: number;
    exhale: number;
    holdAfter: number;
  };
  strictnessTier: 'gentle_nudge' | 'friction_delay' | 'strict_lock';
  lossCurveSimulationEnabled: boolean;
  coolingOffDurationSeconds: number;
}

interface ClientOption {
  id: string;
  name: string;
  domain: BehavioralDomainId;
}

const CLIENT_OPTIONS: ClientOption[] = [
  { id: 'client_usr_882', name: 'Alex M.', domain: 'financial_speculation' },
  { id: 'client_usr_104', name: 'Jordan K.', domain: 'mindful_media' },
  { id: 'client_usr_491', name: 'Taylor S.', domain: 'substance_reduction' },
  { id: 'client_usr_732', name: 'Morgan L.', domain: 'mindful_spending' },
  { id: 'all_clients', name: 'All Clients in Domain (Clinic Template)', domain: 'financial_speculation' },
];

export interface InterventionStudioProps {
  coachId?: string;
  coachName?: string;
}

export const InterventionStudio: React.FC<InterventionStudioProps> = ({
  coachId = 'coach_usr_01',
  coachName = 'Care Lead Practitioner',
}) => {
  const [selectedDomain, setSelectedDomain] = useState<BehavioralDomainId>('financial_speculation');
  const [selectedClientId, setSelectedClientId] = useState<string>('client_usr_882');
  const [recipe, setRecipe] = useState<InterventionRecipe>(() =>
    createDefaultRecipeForDomain('financial_speculation', coachId, coachName)
  );
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(() => recipe.blocks[0]?.id || null);
  const [previewStepIndex, setPreviewStepIndex] = useState<number>(0);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [deploymentNotice, setDeploymentNotice] = useState<string | null>(null);
  const [isVoiceMemoModalOpen, setIsVoiceMemoModalOpen] = useState<boolean>(false);

  // Preview Simulator State
  const [isPlayingAudio, setIsPlayingAudio] = useState<boolean>(false);
  const [audioPlayhead, setAudioPlayhead] = useState<number>(0);
  const [somaticPhase, setSomaticPhase] = useState<'Inhale' | 'Hold' | 'Exhale'>('Inhale');
  const [somaticTimer, setSomaticTimer] = useState<number>(4);
  const [frictionCountdown, setFrictionCountdown] = useState<number>(60);
  const [isFrictionRunning, setIsFrictionRunning] = useState<boolean>(false);
  const [interactiveReflection, setInteractiveReflection] = useState<string>('');
  const [cravingValue, setCravingValue] = useState<number>(7);

  // Synchronize selected block when blocks change
  useEffect(() => {
    if (recipe.blocks.length > 0) {
      if (!selectedBlockId || !recipe.blocks.some((b) => b.id === selectedBlockId)) {
        setSelectedBlockId(recipe.blocks[0].id);
      }
      if (previewStepIndex >= recipe.blocks.length) {
        setPreviewStepIndex(Math.max(0, recipe.blocks.length - 1));
      }
    } else {
      setSelectedBlockId(null);
      setPreviewStepIndex(0);
    }
  }, [recipe.blocks]);

  // Audio Playback Simulation Timer
  useEffect(() => {
    if (!isPlayingAudio) return;
    const interval = setInterval(() => {
      setAudioPlayhead((prev) => (prev >= 45 ? 0 : prev + 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [isPlayingAudio]);

  // Somatic Breathing Simulation Timer
  useEffect(() => {
    const currentBlock = recipe.blocks[previewStepIndex];
    if (currentBlock?.type !== 'somatic') return;

    const interval = setInterval(() => {
      setSomaticTimer((prev) => {
        if (prev > 1) return prev - 1;
        const config = currentBlock.somaticConfig;
        if (config?.variant === 'relaxing_4_7_8') {
          if (somaticPhase === 'Inhale') {
            setSomaticPhase('Hold');
            return 7;
          } else if (somaticPhase === 'Hold') {
            setSomaticPhase('Exhale');
            return 8;
          } else {
            setSomaticPhase('Inhale');
            return 4;
          }
        } else if (config?.variant === 'diaphragmatic_mandala') {
          setSomaticPhase((curr) => (curr === 'Inhale' ? 'Exhale' : 'Inhale'));
          return 5;
        } else {
          // Box 4-4-4
          setSomaticPhase((curr) => (curr === 'Inhale' ? 'Hold' : curr === 'Hold' ? 'Exhale' : 'Inhale'));
          return 4;
        }
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [previewStepIndex, somaticPhase, recipe.blocks]);

  // Friction Cooling-Off Simulation Timer
  useEffect(() => {
    if (!isFrictionRunning) return;
    const interval = setInterval(() => {
      setFrictionCountdown((prev) => {
        if (prev <= 1) {
          setIsFrictionRunning(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isFrictionRunning]);

  // Handler: Change Domain and offer to load preset
  const handleDomainChange = (domain: BehavioralDomainId) => {
    setSelectedDomain(domain);
    const newRecipe = createDefaultRecipeForDomain(domain, 'coach_usr_01', 'Care Lead Practitioner');
    const targetClient = CLIENT_OPTIONS.find((c) => c.id === selectedClientId);
    if (targetClient && targetClient.id !== 'all_clients') {
      newRecipe.clientId = targetClient.id;
      newRecipe.clientName = targetClient.name;
      newRecipe.name = `${targetClient.name} - ${newRecipe.name}`;
    }
    setRecipe(newRecipe);
    setSelectedBlockId(newRecipe.blocks[0]?.id || null);
    setPreviewStepIndex(0);
  };

  // Handler: Change Client
  const handleClientChange = (clientId: string) => {
    setSelectedClientId(clientId);
    const client = CLIENT_OPTIONS.find((c) => c.id === clientId);
    if (client && client.id !== 'all_clients') {
      setRecipe((prev) => ({
        ...prev,
        clientId: client.id,
        clientName: client.name,
        name: `${client.name} - ${DEFAULT_DOMAIN_RECIPES[prev.domainId]?.name || 'Intervention Recipe'}`,
      }));
    }
  };

  // Handler: Reorder Block
  const handleMoveBlock = (fromIndex: number, direction: 'up' | 'down') => {
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= recipe.blocks.length) return;
    const reordered = reorderBlocks(recipe.blocks, fromIndex, toIndex);
    setRecipe((prev) => ({ ...prev, blocks: reordered, updatedAt: new Date().toISOString() }));
  };

  // Handler: Add Block from Catalog
  const handleAddBlock = (type: ComposableBlockType) => {
    let newBlock: ComposableBlock;
    if (type === 'somatic') {
      newBlock = createDefaultSomaticBlock('box_4_4_4');
    } else if (type === 'cognitive') {
      newBlock = createDefaultCognitiveBlock('cbt_reframing');
    } else if (type === 'friction') {
      newBlock = createDefaultFrictionBlock('cooling_off_lock', 60);
    } else {
      newBlock = createDefaultSupportBlock('voice_video_note');
    }

    const updated = addBlockToRecipe(recipe, newBlock);
    setRecipe(updated);
    setSelectedBlockId(newBlock.id);
  };

  // Handler: Remove Block
  const handleRemoveBlock = (blockId: string) => {
    if (recipe.blocks.length <= 1) {
      alert('A recipe must have at least one block.');
      return;
    }
    const updated = removeBlockFromRecipe(recipe, blockId);
    setRecipe(updated);
  };

  // Handler: Save & Deploy Recipe
  const handleSaveAndDeploy = () => {
    const validation = validateRecipe(recipe);
    if (!validation.valid) {
      alert(`Cannot deploy recipe:\n${validation.errors.join('\n')}`);
      return;
    }

    const client = CLIENT_OPTIONS.find((c) => c.id === selectedClientId);
    const assigned = assignRecipeToClient(
      recipe,
      client?.id || 'all_clients',
      client?.name || 'All Clients',
      'coach_usr_01',
      'Care Lead Practitioner'
    );
    assigned.encryptedPayload = `ZK-ENC-${Math.random().toString(36).substring(2, 10).toUpperCase()}`;

    setRecipe(assigned);
    setSaveSuccess(true);
    setDeploymentNotice(
      `✓ Recipe "${assigned.name}" successfully zero-knowledge encrypted & deployed to ${assigned.clientName} (${recipe.blocks.length} composable blocks).`
    );

    setTimeout(() => {
      setSaveSuccess(false);
    }, 4500);
  };

  const selectedBlock = recipe.blocks.find((b) => b.id === selectedBlockId);
  const currentPreviewBlock = recipe.blocks[previewStepIndex] || recipe.blocks[0];

  return (
    <div style={{
      backgroundColor: '#FFFFFF',
      borderRadius: '16px',
      padding: '28px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
      border: '1px solid #E2E8F0',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      {/* Studio Header & Deployment Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '16px', borderBottom: '1px solid #E2E8F0', paddingBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            backgroundColor: '#FEF3C7',
            color: '#D97706',
            borderRadius: '12px',
            width: '46px',
            height: '46px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
          }}>
            🧩
          </div>
          <div>
            <h2 style={{ margin: 0, color: '#1E293B', fontSize: '20px', fontWeight: 800 }}>
              Clinician Recipe Builder Studio
            </h2>
            <p style={{ margin: '2px 0 0', color: '#64748B', fontSize: '13px' }}>
              Assemble modular Somatic, Cognitive, Friction, and Support blocks into personalized clinical protocols
            </p>
          </div>
        </div>

        {/* Save & Deploy Action */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            onClick={() => {
              const reset = createDefaultRecipeForDomain(selectedDomain, 'coach_usr_01', 'Care Lead Practitioner');
              setRecipe(reset);
              setSelectedBlockId(reset.blocks[0]?.id || null);
            }}
            style={{
              backgroundColor: '#FAF8F5',
              color: '#5C524C',
              border: '1px solid #CBD5E1',
              borderRadius: '8px',
              padding: '10px 16px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            ↺ Reset to Domain Template
          </button>
          <button
            onClick={handleSaveAndDeploy}
            style={{
              background: 'linear-gradient(135deg, #1B4332 0%, #2D5A27 100%)',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 22px',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(27, 67, 50, 0.25)',
            }}
          >
            {saveSuccess ? '✓ Recipe Deployed!' : '💾 Save & Assign Recipe to Client'}
          </button>
        </div>
      </div>

      {/* Deployment Banner Notification */}
      {deploymentNotice && (
        <div style={{
          backgroundColor: '#E8F5E9',
          color: '#1B4332',
          border: '1px solid #A5D6A7',
          padding: '12px 18px',
          borderRadius: '10px',
          fontSize: '13px',
          fontWeight: 600,
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <span>{deploymentNotice}</span>
          <button
            onClick={() => setDeploymentNotice(null)}
            style={{ background: 'none', border: 'none', color: '#1B4332', cursor: 'pointer', fontWeight: 800 }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Client & Domain Assignment Configuration Bar */}
      <div style={{
        backgroundColor: '#FAF8F5',
        borderRadius: '12px',
        padding: '16px 20px',
        border: '1px solid #EAE2D7',
        marginBottom: '24px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '16px',
        alignItems: 'center',
      }}>
        {/* Client Selector */}
        <div>
          <label style={{ fontSize: '12px', fontWeight: 700, color: '#2D2826', display: 'block', marginBottom: '4px' }}>
            👤 Target Patient / Client
          </label>
          <select
            value={selectedClientId}
            onChange={(e) => handleClientChange(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #CBD5E1',
              backgroundColor: '#FFFFFF',
              fontSize: '13px',
              fontWeight: 600,
              color: '#2D2826',
            }}
          >
            {CLIENT_OPTIONS.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* Domain Selector */}
        <div>
          <label style={{ fontSize: '12px', fontWeight: 700, color: '#2D2826', display: 'block', marginBottom: '4px' }}>
            🎯 Behavioral Wellness Domain
          </label>
          <select
            value={selectedDomain}
            onChange={(e) => handleDomainChange(e.target.value as BehavioralDomainId)}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #CBD5E1',
              backgroundColor: '#FFFFFF',
              fontSize: '13px',
              fontWeight: 600,
              color: '#2D2826',
            }}
          >
            <option value="financial_speculation">📈 Financial & High-Risk Speculation</option>
            <option value="mindful_media">📱 Social Media & Infinite Feeds</option>
            <option value="mindful_spending">🛍️ Impulse Shopping & Checkout</option>
            <option value="mindful_eating">🥗 Late-Night Food Delivery</option>
            <option value="substance_reduction">🛡️ Substance & Alcohol Reduction</option>
            <option value="digital_dependency">⏳ Digital Dependency & Screen-Time</option>
            <option value="adult_media">🔞 Adult Media & Explicit Content</option>
            <option value="gaming_microtransactions">🎮 Gaming & Loot Microtransactions</option>
            <option value="after_hours_work">💼 After-Hours Work Boundaries</option>
            <option value="anxiety_news">📰 Compulsive News & Panic Scrolling</option>
          </select>
        </div>

        {/* Recipe Title */}
        <div>
          <label style={{ fontSize: '12px', fontWeight: 700, color: '#2D2826', display: 'block', marginBottom: '4px' }}>
            📝 Recipe Title
          </label>
          <input
            type="text"
            value={recipe.name}
            onChange={(e) => setRecipe({ ...recipe, name: e.target.value })}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #CBD5E1',
              backgroundColor: '#FFFFFF',
              fontSize: '13px',
              fontWeight: 600,
              color: '#2D2826',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      {/* Main 3-Column Studio Layout: Catalog/Canvas + Parameter Inspector + Interactive Preview */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.3fr 1.1fr', gap: '24px', alignItems: 'start' }}>
        
        {/* Column 1: Block Catalog & Assembly Canvas */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* Modular Block Catalog */}
          <div style={{ backgroundColor: '#F8FAFC', borderRadius: '12px', padding: '16px', border: '1px solid #E2E8F0' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'block', marginBottom: '10px' }}>
              + Add Building Block to Canvas
            </span>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
              <button
                onClick={() => handleAddBlock('somatic')}
                style={{
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #CBD5E1',
                  backgroundColor: '#FFFFFF',
                  color: '#1B4332',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <span>🌊</span> + Somatic Breath
              </button>
              <button
                onClick={() => handleAddBlock('cognitive')}
                style={{
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #CBD5E1',
                  backgroundColor: '#FFFFFF',
                  color: '#0369A1',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <span>🧠</span> + Cognitive CBT
              </button>
              <button
                onClick={() => handleAddBlock('friction')}
                style={{
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #CBD5E1',
                  backgroundColor: '#FFFFFF',
                  color: '#B45309',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <span>⏳</span> + Friction Delay
              </button>
              <button
                onClick={() => handleAddBlock('support')}
                style={{
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #CBD5E1',
                  backgroundColor: '#FFFFFF',
                  color: '#DC2626',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <span>🎙️</span> + Support & 988
              </button>
            </div>
          </div>

          {/* Interactive Assembly Canvas */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 800, color: '#1E293B' }}>
                Sequential Block Sequence ({recipe.blocks.length} Steps)
              </h3>
              <span style={{ fontSize: '11px', color: '#64748B' }}>
                Executed step-by-step
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {recipe.blocks.map((block, index) => {
                const isSelected = block.id === selectedBlockId;
                const isFirst = index === 0;
                const isLast = index === recipe.blocks.length - 1;

                let icon = '🌊';
                let typeColor = '#1B4332';
                let typeBg = '#EBF5F1';
                if (block.type === 'cognitive') {
                  icon = '🧠';
                  typeColor = '#0369A1';
                  typeBg = '#E0F2FE';
                } else if (block.type === 'friction') {
                  icon = '⏳';
                  typeColor = '#B45309';
                  typeBg = '#FEF3C7';
                } else if (block.type === 'support') {
                  icon = '🎙️';
                  typeColor = '#DC2626';
                  typeBg = '#FEE2E2';
                }

                return (
                  <div
                    key={block.id}
                    onClick={() => setSelectedBlockId(block.id)}
                    style={{
                      backgroundColor: isSelected ? '#FFFFFF' : '#FAF8F5',
                      borderRadius: '12px',
                      padding: '14px',
                      border: `2px solid ${isSelected ? typeColor : '#EAE2D7'}`,
                      cursor: 'pointer',
                      boxShadow: isSelected ? '0 4px 14px rgba(0,0,0,0.08)' : 'none',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{
                          backgroundColor: typeBg,
                          color: typeColor,
                          fontWeight: 800,
                          fontSize: '11px',
                          padding: '3px 8px',
                          borderRadius: '6px',
                        }}>
                          #{index + 1}
                        </span>
                        <span style={{ fontSize: '18px' }}>{icon}</span>
                        <div>
                          <strong style={{ fontSize: '13px', color: '#1E293B', display: 'block' }}>
                            {block.title}
                          </strong>
                          <span style={{ fontSize: '11px', color: '#64748B' }}>
                            {block.type === 'somatic' && `${block.somaticConfig?.variant} • ${block.somaticConfig?.cycles} cycles`}
                            {block.type === 'cognitive' && `${block.cognitiveConfig?.variant}`}
                            {block.type === 'friction' && `${block.frictionConfig?.lockDurationSeconds}s lock delay`}
                            {block.type === 'support' && `${block.supportConfig?.variant}`}
                          </span>
                        </div>
                      </div>

                      {/* Reorder and Delete Controls */}
                      <div style={{ display: 'flex', gap: '4px' }} onClick={(e) => e.stopPropagation()}>
                        <button
                          disabled={isFirst}
                          onClick={() => handleMoveBlock(index, 'up')}
                          aria-label={`Move Step ${index + 1} Up`}
                          style={{
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #CBD5E1',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            cursor: isFirst ? 'not-allowed' : 'pointer',
                            opacity: isFirst ? 0.35 : 1,
                            fontSize: '12px',
                            fontWeight: 700,
                          }}
                        >
                          ↑
                        </button>
                        <button
                          disabled={isLast}
                          onClick={() => handleMoveBlock(index, 'down')}
                          aria-label={`Move Step ${index + 1} Down`}
                          style={{
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #CBD5E1',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            cursor: isLast ? 'not-allowed' : 'pointer',
                            opacity: isLast ? 0.35 : 1,
                            fontSize: '12px',
                            fontWeight: 700,
                          }}
                        >
                          ↓
                        </button>
                        <button
                          onClick={() => handleRemoveBlock(block.id)}
                          aria-label={`Remove Step ${index + 1}`}
                          style={{
                            backgroundColor: '#FFF5F5',
                            border: '1px solid #FED7D7',
                            color: '#DC2626',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 700,
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Column 2: Block Parameter Inspector */}
        <div style={{
          backgroundColor: '#FAF8F5',
          borderRadius: '16px',
          padding: '20px',
          border: '1px solid #EAE2D7',
        }}>
          {selectedBlock ? (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #EAE2D7', paddingBottom: '12px', marginBottom: '16px' }}>
                <div>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>
                    Step Inspector: {selectedBlock.type}
                  </span>
                  <h3 style={{ margin: '2px 0 0', fontSize: '16px', fontWeight: 800, color: '#1E293B' }}>
                    Configure {selectedBlock.title}
                  </h3>
                </div>
              </div>

              {/* Block Title & Instructions */}
              <div style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#1E293B', display: 'block', marginBottom: '4px' }}>
                  Block Display Title
                </label>
                <input
                  type="text"
                  value={selectedBlock.title}
                  onChange={(e) =>
                    setRecipe(updateBlockInRecipe(recipe, selectedBlock.id, { title: e.target.value }))
                  }
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: '6px',
                    border: '1px solid #CBD5E1',
                    fontSize: '12px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#1E293B', display: 'block', marginBottom: '4px' }}>
                  Step Guidance / Instructions
                </label>
                <textarea
                  rows={2}
                  value={selectedBlock.instructions}
                  onChange={(e) =>
                    setRecipe(updateBlockInRecipe(recipe, selectedBlock.id, { instructions: e.target.value }))
                  }
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: '6px',
                    border: '1px solid #CBD5E1',
                    fontSize: '12px',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* SOMATIC PARAMETERS */}
              {selectedBlock.type === 'somatic' && selectedBlock.somaticConfig && (
                <div style={{ backgroundColor: '#FFFFFF', padding: '14px', borderRadius: '10px', border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 700, color: '#1E293B', display: 'block', marginBottom: '4px' }}>
                      Breathing Pattern Variant
                    </label>
                    <select
                      value={selectedBlock.somaticConfig.variant}
                      onChange={(e) => {
                        const variant = e.target.value as SomaticBlockConfig['variant'];
                        let inhale = 4;
                        let hold = 4;
                        let exhale = 4;
                        if (variant === 'relaxing_4_7_8') {
                          inhale = 4;
                          hold = 7;
                          exhale = 8;
                        } else if (variant === 'diaphragmatic_mandala') {
                          inhale = 5;
                          hold = 0;
                          exhale = 5;
                        }
                        setRecipe(updateBlockInRecipe(recipe, selectedBlock.id, {
                          somaticConfig: {
                            ...selectedBlock.somaticConfig!,
                            variant,
                            inhaleSeconds: inhale,
                            holdSeconds: hold,
                            exhaleSeconds: exhale,
                          },
                        }));
                      }}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12px' }}
                    >
                      <option value="box_4_4_4">Box Breathing (4-4-4-4)</option>
                      <option value="relaxing_4_7_8">Relaxing Parasympathetic (4-7-8)</option>
                      <option value="diaphragmatic_mandala">Diaphragmatic Mandala (5-0-5)</option>
                    </select>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                    <div>
                      <span style={{ fontSize: '11px', color: '#64748B' }}>Inhale ({selectedBlock.somaticConfig.inhaleSeconds}s)</span>
                      <input
                        type="range"
                        min="2"
                        max="8"
                        value={selectedBlock.somaticConfig.inhaleSeconds}
                        onChange={(e) =>
                          setRecipe(updateBlockInRecipe(recipe, selectedBlock.id, {
                            somaticConfig: { ...selectedBlock.somaticConfig!, inhaleSeconds: Number(e.target.value) },
                          }))
                        }
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <span style={{ fontSize: '11px', color: '#64748B' }}>Hold ({selectedBlock.somaticConfig.holdSeconds}s)</span>
                      <input
                        type="range"
                        min="0"
                        max="8"
                        value={selectedBlock.somaticConfig.holdSeconds}
                        onChange={(e) =>
                          setRecipe(updateBlockInRecipe(recipe, selectedBlock.id, {
                            somaticConfig: { ...selectedBlock.somaticConfig!, holdSeconds: Number(e.target.value) },
                          }))
                        }
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <span style={{ fontSize: '11px', color: '#64748B' }}>Exhale ({selectedBlock.somaticConfig.exhaleSeconds}s)</span>
                      <input
                        type="range"
                        min="2"
                        max="8"
                        value={selectedBlock.somaticConfig.exhaleSeconds}
                        onChange={(e) =>
                          setRecipe(updateBlockInRecipe(recipe, selectedBlock.id, {
                            somaticConfig: { ...selectedBlock.somaticConfig!, exhaleSeconds: Number(e.target.value) },
                          }))
                        }
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>

                  <div>
                    <span style={{ fontSize: '11px', color: '#64748B' }}>Cycles: {selectedBlock.somaticConfig.cycles}</span>
                    <input
                      type="range"
                      min="1"
                      max="8"
                      value={selectedBlock.somaticConfig.cycles}
                      onChange={(e) =>
                        setRecipe(updateBlockInRecipe(recipe, selectedBlock.id, {
                          somaticConfig: { ...selectedBlock.somaticConfig!, cycles: Number(e.target.value) },
                        }))
                      }
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
              )}

              {/* COGNITIVE PARAMETERS */}
              {selectedBlock.type === 'cognitive' && selectedBlock.cognitiveConfig && (
                <div style={{ backgroundColor: '#FFFFFF', padding: '14px', borderRadius: '10px', border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 700, color: '#1E293B', display: 'block', marginBottom: '4px' }}>
                      CBT Inquiry Variant
                    </label>
                    <select
                      value={selectedBlock.cognitiveConfig.variant}
                      onChange={(e) => {
                        const variant = e.target.value as CognitiveBlockConfig['variant'];
                        setRecipe(updateBlockInRecipe(recipe, selectedBlock.id, {
                          cognitiveConfig: { ...selectedBlock.cognitiveConfig!, variant },
                        }));
                      }}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12px' }}
                    >
                      <option value="cbt_reframing">CBT Thought Reframing</option>
                      <option value="craving_vs_need">Craving vs. Genuine Need Slider</option>
                      <option value="loss_probability">Loss Probability & Consequence Perspective</option>
                      <option value="urge_surfing">Mindful Urge Surfing</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 700, color: '#1E293B', display: 'block', marginBottom: '4px' }}>
                      Prompt Question
                    </label>
                    <textarea
                      rows={3}
                      value={selectedBlock.cognitiveConfig.promptQuestion}
                      onChange={(e) =>
                        setRecipe(updateBlockInRecipe(recipe, selectedBlock.id, {
                          cognitiveConfig: { ...selectedBlock.cognitiveConfig!, promptQuestion: e.target.value },
                        }))
                      }
                      style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12px', fontFamily: 'inherit', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
              )}

              {/* FRICTION PARAMETERS */}
              {selectedBlock.type === 'friction' && selectedBlock.frictionConfig && (
                <div style={{ backgroundColor: '#FFFFFF', padding: '14px', borderRadius: '10px', border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 700, color: '#1E293B', display: 'block', marginBottom: '4px' }}>
                      Friction Delay Duration: {selectedBlock.frictionConfig.lockDurationSeconds}s
                    </label>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {[30, 60, 120, 300].map((s) => (
                        <button
                          key={s}
                          onClick={() =>
                            setRecipe(updateBlockInRecipe(recipe, selectedBlock.id, {
                              frictionConfig: { ...selectedBlock.frictionConfig!, lockDurationSeconds: s },
                            }))
                          }
                          style={{
                            flex: 1,
                            padding: '6px',
                            borderRadius: '6px',
                            border: selectedBlock.frictionConfig!.lockDurationSeconds === s ? '1.5px solid #D97706' : '1px solid #CBD5E1',
                            backgroundColor: selectedBlock.frictionConfig!.lockDurationSeconds === s ? '#FEF3C7' : '#FAF8F5',
                            color: '#1E293B',
                            fontWeight: 700,
                            fontSize: '11px',
                            cursor: 'pointer',
                          }}
                        >
                          {s}s
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 700, color: '#1E293B', display: 'block', marginBottom: '4px' }}>
                      Pledge Commitment Text
                    </label>
                    <input
                      type="text"
                      value={selectedBlock.frictionConfig.pledgeText || ''}
                      onChange={(e) =>
                        setRecipe(updateBlockInRecipe(recipe, selectedBlock.id, {
                          frictionConfig: { ...selectedBlock.frictionConfig!, pledgeText: e.target.value },
                        }))
                      }
                      style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12px', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
              )}

              {/* SUPPORT PARAMETERS */}
              {selectedBlock.type === 'support' && selectedBlock.supportConfig && (
                <div style={{ backgroundColor: '#FFFFFF', padding: '14px', borderRadius: '10px', border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 700, color: '#1E293B', display: 'block', marginBottom: '4px' }}>
                      Support Type
                    </label>
                    <select
                      value={selectedBlock.supportConfig.variant}
                      onChange={(e) => {
                        const variant = e.target.value as SupportBlockConfig['variant'];
                        setRecipe(updateBlockInRecipe(recipe, selectedBlock.id, {
                          supportConfig: { ...selectedBlock.supportConfig!, variant },
                        }));
                      }}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12px' }}
                    >
                      <option value="voice_video_note">Therapist Voice / Video Note</option>
                      <option value="crisis_988">988 Suicide & Crisis Lifeline</option>
                      <option value="sponsor_dialer">Care Circle Sponsor Dialer</option>
                    </select>
                  </div>

                  {selectedBlock.supportConfig.variant === 'voice_video_note' && (
                    <div style={{ backgroundColor: '#F8FAFC', padding: '12px', borderRadius: '8px', border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#334155' }}>
                          🎙️ Attached Audio Memo
                        </span>
                        <button
                          type="button"
                          onClick={() => setIsVoiceMemoModalOpen(true)}
                          style={{
                            backgroundColor: selectedBlock.supportConfig.mediaUrl ? '#E0F2FE' : '#E8F5E9',
                            color: selectedBlock.supportConfig.mediaUrl ? '#0369A1' : '#166534',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '5px 10px',
                            fontSize: '11px',
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          {selectedBlock.supportConfig.mediaUrl ? '🎙️ Replace Audio' : '🎙️ Record or Attach Audio'}
                        </button>
                      </div>

                      {selectedBlock.supportConfig.mediaUrl ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <audio controls src={selectedBlock.supportConfig.mediaUrl} style={{ width: '100%', height: '36px' }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '10px', color: '#64748B' }}>
                            <span>
                              {selectedBlock.supportConfig.durationSeconds !== undefined ? `⏱️ ${selectedBlock.supportConfig.durationSeconds.toFixed(1)}s` : 'Audio Ready'}
                              {selectedBlock.supportConfig.storagePath ? ` • ☁️ ${selectedBlock.supportConfig.storagePath.split('/').pop()}` : ''}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setRecipe(updateBlockInRecipe(recipe, selectedBlock.id, {
                                  supportConfig: {
                                    ...selectedBlock.supportConfig!,
                                    mediaUrl: undefined,
                                    durationSeconds: undefined,
                                    storagePath: undefined,
                                  },
                                }));
                              }}
                              style={{ background: 'none', border: 'none', color: '#DC2626', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ fontSize: '11px', color: '#64748B' }}>
                          No audio attached. Click above to record live in browser or upload an audio file.
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 700, color: '#1E293B', display: 'block', marginBottom: '4px' }}>
                      Media Title / Description
                    </label>
                    <input
                      type="text"
                      value={selectedBlock.supportConfig.mediaTitle || ''}
                      onChange={(e) =>
                        setRecipe(updateBlockInRecipe(recipe, selectedBlock.id, {
                          supportConfig: { ...selectedBlock.supportConfig!, mediaTitle: e.target.value },
                        }))
                      }
                      style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12px', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 700, color: '#1E293B', display: 'block', marginBottom: '4px' }}>
                      Live Synchronized Transcript Preview
                    </label>
                    <textarea
                      rows={2}
                      value={selectedBlock.supportConfig.transcript || ''}
                      onChange={(e) =>
                        setRecipe(updateBlockInRecipe(recipe, selectedBlock.id, {
                          supportConfig: { ...selectedBlock.supportConfig!, transcript: e.target.value },
                        }))
                      }
                      style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '12px', fontFamily: 'inherit', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 10px', color: '#64748B' }}>
              Select a block from the canvas to configure parameters.
            </div>
          )}
        </div>

        {/* Column 3: Sequential Step-by-Step Interactive Preview Player */}
        <div style={{
          backgroundColor: '#0F172A',
          borderRadius: '24px',
          padding: '24px',
          color: '#FFFFFF',
          boxShadow: '0 12px 36px rgba(0,0,0,0.25)',
          border: '3px solid #1E293B',
          minHeight: '480px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}>
          <div>
            {/* Mobile Header Bar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', borderBottom: '1px solid #1E293B', paddingBottom: '10px' }}>
              <span style={{ fontSize: '11px', color: '#10B981', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                ● Client Live Shield
              </span>
              <span style={{ fontSize: '11px', backgroundColor: '#1E293B', padding: '3px 8px', borderRadius: '6px', color: '#94A3B8' }}>
                Step {previewStepIndex + 1} of {recipe.blocks.length}
              </span>
            </div>

            {/* Current Step Content */}
            {currentPreviewBlock ? (
              <div>
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <h4 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: 700, color: '#F1F5F9' }}>
                    {currentPreviewBlock.title}
                  </h4>
                  <p style={{ margin: 0, fontSize: '12px', color: '#94A3B8' }}>
                    {currentPreviewBlock.instructions}
                  </p>
                </div>

                {/* SOMATIC PREVIEW */}
                {currentPreviewBlock.type === 'somatic' && (
                  <div style={{ textAlign: 'center', margin: '20px 0' }}>
                    <div style={{
                      width: '100px',
                      height: '100px',
                      borderRadius: '50%',
                      margin: '0 auto 12px',
                      backgroundColor: somaticPhase === 'Inhale' ? '#3B82F6' : somaticPhase === 'Hold' ? '#EAB308' : '#10B981',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 0 24px rgba(16, 185, 129, 0.3)',
                      transition: 'transform 1s ease',
                      transform: somaticPhase === 'Inhale' ? 'scale(1.15)' : somaticPhase === 'Hold' ? 'scale(1.15)' : 'scale(0.9)',
                    }}>
                      <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase' }}>{somaticPhase}</span>
                      <span style={{ fontSize: '18px', fontWeight: 800, fontFamily: 'monospace' }}>00:0{somaticTimer}</span>
                    </div>
                    <span style={{ fontSize: '11px', color: '#94A3B8' }}>
                      Follow the visual breathing mandala
                    </span>
                  </div>
                )}

                {/* COGNITIVE PREVIEW */}
                {currentPreviewBlock.type === 'cognitive' && (
                  <div style={{ backgroundColor: '#1E293B', borderRadius: '12px', padding: '14px', margin: '14px 0', border: '1px solid #334155' }}>
                    <div style={{ fontSize: '12.5px', color: '#F1F5F9', fontStyle: 'italic', marginBottom: '10px' }}>
                      "{currentPreviewBlock.cognitiveConfig?.promptQuestion}"
                    </div>

                    {currentPreviewBlock.cognitiveConfig?.variant === 'craving_vs_need' ? (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#94A3B8', marginBottom: '4px' }}>
                          <span>Craving: {cravingValue}/10</span>
                          <span>{cravingValue <= 3 ? 'Mild' : cravingValue <= 7 ? 'Moderate' : 'Acute'}</span>
                        </div>
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={cravingValue}
                          onChange={(e) => setCravingValue(Number(e.target.value))}
                          style={{ width: '100%', accentColor: '#10B981' }}
                        />
                      </div>
                    ) : (
                      <textarea
                        rows={2}
                        placeholder="Type your grounding reflection..."
                        value={interactiveReflection}
                        onChange={(e) => setInteractiveReflection(e.target.value)}
                        style={{
                          width: '100%',
                          backgroundColor: '#0F172A',
                          color: '#FFFFFF',
                          border: '1px solid #334155',
                          borderRadius: '6px',
                          padding: '6px 8px',
                          fontSize: '11px',
                          fontFamily: 'inherit',
                          boxSizing: 'border-box',
                        }}
                      />
                    )}
                  </div>
                )}

                {/* FRICTION PREVIEW */}
                {currentPreviewBlock.type === 'friction' && (
                  <div style={{ backgroundColor: '#1E293B', borderRadius: '12px', padding: '14px', margin: '14px 0', textAlign: 'center', border: '1px solid #334155' }}>
                    <div style={{ fontSize: '20px', fontWeight: 800, color: '#F59E0B', fontFamily: 'monospace', marginBottom: '6px' }}>
                      00:{frictionCountdown < 10 ? `0${frictionCountdown}` : frictionCountdown}
                    </div>
                    <p style={{ margin: '0 0 10px', fontSize: '11.5px', color: '#CBD5E1' }}>
                      {currentPreviewBlock.frictionConfig?.pledgeText || 'Pausing with intention.'}
                    </p>
                    <button
                      onClick={() => setIsFrictionRunning(!isFrictionRunning)}
                      style={{
                        backgroundColor: isFrictionRunning ? '#EF4444' : '#10B981',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '11px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      {isFrictionRunning ? 'Pause Timer' : 'Start Cooling Delay'}
                    </button>
                  </div>
                )}

                {/* SUPPORT PREVIEW */}
                {currentPreviewBlock.type === 'support' && (
                  <div style={{ backgroundColor: '#1E293B', borderRadius: '12px', padding: '14px', margin: '14px 0', border: '1px solid #334155' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#F1F5F9' }}>
                          {currentPreviewBlock.supportConfig?.mediaTitle || 'Mindful Voice Note'}
                        </div>
                        <div style={{ fontSize: '10px', color: '#94A3B8' }}>
                          0:{audioPlayhead < 10 ? `0${audioPlayhead}` : audioPlayhead} / 0:45
                        </div>
                      </div>
                      <button
                        onClick={() => setIsPlayingAudio(!isPlayingAudio)}
                        style={{
                          backgroundColor: isPlayingAudio ? '#EF4444' : '#10B981',
                          color: '#FFFFFF',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '4px 10px',
                          fontSize: '11px',
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        {isPlayingAudio ? 'Stop' : 'Play'}
                      </button>
                    </div>

                    <div style={{ backgroundColor: '#0F172A', borderRadius: '6px', padding: '8px', fontSize: '11px', color: '#94A3B8', fontStyle: 'italic' }}>
                      {currentPreviewBlock.supportConfig?.transcript || 'Take a slow, deep breath...'}
                    </div>

                    {currentPreviewBlock.supportConfig?.variant === 'crisis_988' && (
                      <div style={{ marginTop: '10px', textAlign: 'center' }}>
                        <a
                          href="tel:988"
                          style={{
                            display: 'block',
                            backgroundColor: '#DC2626',
                            color: '#FFFFFF',
                            textDecoration: 'none',
                            padding: '8px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 700,
                          }}
                        >
                          📞 Tap to Call 988 Lifeline
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {/* Sequential Step Navigation Controls */}
          <div>
            {/* Step Dots */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginBottom: '12px' }}>
              {recipe.blocks.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setPreviewStepIndex(idx)}
                  aria-label={`Jump to Step ${idx + 1}`}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: previewStepIndex === idx ? '#10B981' : '#334155',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                  }}
                />
              ))}
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                disabled={previewStepIndex === 0}
                onClick={() => setPreviewStepIndex((p) => Math.max(0, p - 1))}
                style={{
                  flex: 1,
                  backgroundColor: previewStepIndex === 0 ? '#1E293B' : '#334155',
                  color: previewStepIndex === 0 ? '#64748B' : '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: previewStepIndex === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                ← Prev Block
              </button>
              <button
                disabled={previewStepIndex >= recipe.blocks.length - 1}
                onClick={() => setPreviewStepIndex((p) => Math.min(recipe.blocks.length - 1, p + 1))}
                style={{
                  flex: 1,
                  backgroundColor: previewStepIndex >= recipe.blocks.length - 1 ? '#1E293B' : '#10B981',
                  color: previewStepIndex >= recipe.blocks.length - 1 ? '#64748B' : '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: previewStepIndex >= recipe.blocks.length - 1 ? 'not-allowed' : 'pointer',
                }}
              >
                Next Block →
              </button>
            </div>
          </div>
        </div>

        {selectedBlock && selectedBlock.type === 'support' && selectedBlock.supportConfig && (
          <VoiceMemoModal
            isOpen={isVoiceMemoModalOpen}
            onClose={() => setIsVoiceMemoModalOpen(false)}
            onSave={(memo) => {
              setRecipe(updateBlockInRecipe(recipe, selectedBlock.id, {
                supportConfig: {
                  ...selectedBlock.supportConfig!,
                  mediaUrl: memo.mediaUrl,
                  mediaTitle: memo.mediaTitle,
                  transcript: memo.transcript,
                  durationSeconds: memo.durationSeconds,
                  storagePath: memo.storagePath,
                  mediaType: 'audio',
                },
              }));
              setDeploymentNotice('✓ Voice Memo Attached to Support Block');
              setTimeout(() => setDeploymentNotice(null), 3000);
            }}
            coachId={coachId}
            coachName={coachName}
            patientId={selectedClientId === 'all_clients' ? 'client_template' : selectedClientId}
            patientName={CLIENT_OPTIONS.find((c) => c.id === selectedClientId)?.name || 'Client'}
            domainId={selectedDomain}
            domainName={selectedDomain.replace('_', ' ')}
            initialTitle={selectedBlock.supportConfig.mediaTitle || 'Therapist Supportive Voice Memo'}
            initialTranscript={selectedBlock.supportConfig.transcript || ''}
            initialMediaUrl={selectedBlock.supportConfig.mediaUrl}
            initialStoragePath={selectedBlock.supportConfig.storagePath}
            initialDuration={selectedBlock.supportConfig.durationSeconds}
          />
        )}
      </div>
    </div>
  );
};

export default InterventionStudio;
